/**
 * Form state of «Зафиксировать» (design QuickCapture.dc.html `defaults`, `buildEv`;
 * docs/research/design2-shell-capture.md §3.2–3.4): defaults per event type, presets, the
 * currency each money field shows (always the one sent, fixing the prototype) and the event.
 */
import {
  NEW_REF_PREFIX,
  SEVERANCE_OPTIONS,
  addMonths,
  compactDecimalRu,
  isRateFormat,
  minorToInput,
  parseHours,
  parseMoney,
  parseRate,
  periodStart,
  rateToInput,
} from '@outsource/shared';
import type {
  CaptureEventInput,
  CaptureLookups,
  CapturePrice,
  CaptureType,
  LookupPay,
  SeveranceOption,
} from '@outsource/shared';
import { leaveEntityIds } from './fieldItems';

export type FormValues = Readonly<Record<string, string>>;

export interface FormState {
  readonly type: CaptureType | null;
  readonly values: FormValues;
  readonly optional: boolean;
}

export const FIRST_FIELD: Readonly<Record<CaptureType, string>> = {
  join: 'person',
  leave: 'person',
  project: 'name',
  rate: 'target',
  person: 'person',
  status: 'project',
  payment: 'client',
  note: 'text',
  dismiss: 'person',
  client: 'name',
  employ: 'person',
  absence: 'person',
  edit: 'person',
  extra: 'person',
};

const DEFAULT_CURRENCY = 'USD';
type PriceFormat = CapturePrice['format'];

/** The values of a `checks` field (V234): joined with «,» in the form. */
export const listOf = (value: string | undefined): string[] => (value ? value.split(',').filter(Boolean) : []);

const SHARE_PER_PERCENT = 10_000;

/**
 * The rate form's values for a project's current pay (V255): «Изменение ставки человека» opens with
 * the conditions in force and the entities whose salary the project is credited with.
 */
export function payFormValues(pay: LookupPay | null, creditEntityIds: readonly string[]): Record<string, string> {
  const credit = { credit: creditEntityIds.join(',') };
  if (!pay) return { ...credit, rateFmt: '', rateV: '', share: '', issuer: '' };
  const share = pay.shareE6 != null ? compactDecimalRu(pay.shareE6 / SHARE_PER_PERCENT) : '';
  const rateV = pay.rateE4 != null ? rateToInput(pay.rateE4) : pay.amountMinor != null ? minorToInput(pay.amountMinor) : '';
  return { ...credit, rateFmt: pay.format, rateV, rateVCur: pay.currency, share, issuer: pay.issuerId ?? '' };
}

/** Entities whose salary can be credited against a project: those paying the person a salary (V255). */
export const creditEntitiesOf = (lookups: CaptureLookups, personId: string | null): string[] =>
  lookups.people.find((p) => p.id === personId)?.salaries.map((s) => s.issuerId) ?? [];

export const firstOfMonth = (today: string): string => periodStart(today.slice(0, 7));
export const firstOfNextMonth = (today: string): string => periodStart(addMonths(today.slice(0, 7), 1));

export function defaults(type: CaptureType | null, today: string): FormValues {
  const base = { from: today, to: today, date: today };
  switch (type) {
    case 'project':
      return { ...base, billing: 'hourly' };
    case 'join':
      return { ...base, rateFmt: 'hourly' };
    case 'rate':
      return { ...base, tkind: 'project', where: 'project', from: firstOfNextMonth(today) };
    case 'status':
      return { ...base, status: 'paused' };
    case 'person':
      return { ...base, kind: 'contractor', format: 'hourly' };
    case 'dismiss':
      return { ...base, lastDay: today, severance: 'none' };
    case 'absence':
      return { ...base, kind: 'vacation' };
    case 'extra':
      return { ...base, kind: 'bonus' };
    default:
      return base;
  }
}

/** A preset (from Состав or a card) merged over the type's defaults. */
export function initialState(preset: Readonly<Record<string, unknown>>, today: string): FormState {
  const type = (preset.type as CaptureType | undefined) ?? null;
  const given = Object.fromEntries(
    Object.entries(preset)
      .filter(([key, value]) => key !== 'type' && value != null)
      .map(([key, value]) => [key, String(value)]),
  );
  return { type, values: { ...defaults(type, today), ...given }, optional: 'plan' in given };
}

export const realId = (value: string | undefined): string | null =>
  value && !value.startsWith(NEW_REF_PREFIX) ? value : null;

const orNull = (value: string | undefined): string | null => (value && value.trim() ? value : null);
/** The severance picked for a dismissal (V292); none unless a known option was picked. */
const severanceOf = (value: string | undefined): SeveranceOption => SEVERANCE_OPTIONS.find((option) => option === value) ?? 'none';

/** The currency of the person's latest rate on a project (V245); null without one. */
const personPayCurrency = (lookups: CaptureLookups, personId: string | null): string | null =>
  lookups.people.find((p) => p.id === personId)?.payCurrency ?? null;

/** The currency of the person's salary through the entity (V259); null without one. */
const salaryCurrency = (lookups: CaptureLookups, personId: string | null, issuerId: string | undefined): string | null =>
  lookups.people.find((p) => p.id === personId)?.salaries.find((s) => s.issuerId === issuerId)?.currency ?? null;

function clientCurrency(lookups: CaptureLookups, clientId: string | null): string | null {
  return lookups.clients.find((c) => c.id === clientId)?.currency ?? null;
}

/** A payment is in its document's currency (V187), else the client's. */
function paymentCurrency(key: string, v: FormValues, lookups: CaptureLookups): string | null {
  const invoice = lookups.invoices.find((i) => i.id === v.invoice);
  if (invoice) return invoice.currency;
  return key === 'fee' && v.amountCur ? v.amountCur : clientCurrency(lookups, realId(v.client));
}

const projectClientId = (lookups: CaptureLookups, projectId: string | null): string | null =>
  lookups.projects.find((p) => p.id === projectId)?.clientId ?? null;

/** What a money field shows before the user picks a currency (design defaults, made consistent). */
function defaultCurrency(key: string, state: FormState, lookups: CaptureLookups): string {
  const v = state.values;
  const byType: Partial<Record<CaptureType, () => string | null>> = {
    join: () => personPayCurrency(lookups, realId(v.person)),
    person: () => personPayCurrency(lookups, realId(v.person)),
    project: () => clientCurrency(lookups, realId(v.client)),
    rate: () =>
      v.tkind === 'project'
        ? clientCurrency(lookups, projectClientId(lookups, realId(v.target)))
        : v.where === 'entity'
          ? salaryCurrency(lookups, realId(v.target), v.entity)
          : personPayCurrency(lookups, realId(v.target)),
    payment: () => paymentCurrency(key, v, lookups),
    extra: () => salaryCurrency(lookups, realId(v.person), v.entity),
  };
  return (state.type && byType[state.type]?.()) ?? DEFAULT_CURRENCY;
}

export function currencyOf(key: string, state: FormState, lookups: CaptureLookups): string {
  return state.values[`${key}Cur`] || defaultCurrency(key, state, lookups);
}

/** Rate format of «Изменение ставки» when the user has not picked one: the current one. */
export function rateFormatOf(state: FormState, lookups: CaptureLookups): PriceFormat {
  const v = state.values;
  if (v.rateFmt) return v.rateFmt as PriceFormat;
  if (v.tkind === 'assign') {
    const format = lookups.assignments.find((a) => a.id === v.assignment)?.payFormat;
    return format && format !== 'hourly' ? (format as PriceFormat) : 'hourly';
  }
  return 'hourly';
}

/** "26,5" / "1 800" → stored units for the format; null when empty, invalid or not positive. */
export function parseAmount(text: string | undefined, format: PriceFormat): number | null {
  const clean = (text ?? '').replace(/[\s\u00a0\u202f]/g, '');
  if (!clean) return null;
  try {
    const value = isRateFormat(format) ? parseRate(clean) : parseMoney(clean);
    return value > 0 ? value : null;
  } catch {
    return null;
  }
}

export function parseMinutes(text: string | undefined): number | null {
  const clean = (text ?? '').replace(/[\s\u00a0\u202f]/g, '');
  if (!clean) return null;
  try {
    const minutes = parseHours(clean);
    return minutes > 0 ? minutes : null;
  } catch {
    return null;
  }
}

const NO_PRICE = { rateE4: null, amountMinor: null, shareE6: null, issuerId: null } as const;
const SHARE_SCALE = 10_000;
const MAX_PERCENT = 100;

/** «10» / «12,5» % → × 1 000 000; null when empty, invalid or out of 0–100 (V169). */
function parseShare(text: string | undefined): number | null {
  const value = Number((text ?? '').replace(/[\s%]/g, '').replace(',', '.'));
  if (!(text ?? '').trim() || !Number.isFinite(value) || value <= 0 || value > MAX_PERCENT) return null;
  return Math.round(value * SHARE_SCALE);
}

function price(text: string | undefined, format: PriceFormat, currency: string): CapturePrice | null {
  const amount = parseAmount(text, format);
  if (amount == null) return null;
  const cur = currency as CapturePrice['currency'];
  return isRateFormat(format) ? { ...NO_PRICE, format, rateE4: amount, currency: cur } : { ...NO_PRICE, format, amountMinor: amount, currency: cur };
}

/** A person's pay on a project: an amount, a share, or the salary through an entity (V169). */
function assignPrice(v: FormValues, format: PriceFormat, currency: string): CapturePrice | null {
  const cur = currency as CapturePrice['currency'];
  if (format === 'revenue_share' || format === 'profit_share') {
    const shareE6 = parseShare(v.share);
    return shareE6 == null ? null : { ...NO_PRICE, format, shareE6, currency: cur };
  }
  if (format === 'salary') return v.issuer ? { ...NO_PRICE, format, issuerId: v.issuer, shareE6: parseShare(v.share), currency: cur } : null;
  return price(v.rateV, format, currency);
}

const PROJECT_FORMAT: Readonly<Record<string, PriceFormat>> = { hourly: 'hourly', scope: 'scope' };

function rateTarget(v: FormValues): string | null {
  if (v.tkind === 'project') return v.target ? `p:${v.target}` : null;
  return v.assignment ? `a:${v.assignment}` : null;
}

/** «Юрлицо» (V259): the person's оклад through the entity as a monthly amount. */
function salaryEvent(state: FormState, lookups: CaptureLookups): CaptureEventInput {
  const v = state.values;
  return {
    type: 'rate',
    target: v.entity ? `e:${v.entity}` : null,
    person: realId(v.target),
    price: price(v.salaryV, 'monthly', currencyOf('salaryV', state, lookups)),
    from: v.from ?? '',
    creditEntities: null,
  };
}

function rateEvent(state: FormState, lookups: CaptureLookups): CaptureEventInput {
  const v = state.values;
  if (v.tkind === 'assign' && v.where === 'entity') return salaryEvent(state, lookups);
  const billing = lookups.projects.find((p) => p.id === v.target)?.billingMode ?? 'hourly';
  const format =
    v.tkind === 'project' ? (PROJECT_FORMAT[billing] ?? 'monthly') : rateFormatOf(state, lookups);
  const currency = currencyOf('rateV', state, lookups);
  // The credit is sent only when the form offered its ticks: an own rate of someone on a salary (V255).
  const offersCredit = v.tkind === 'assign' && format !== 'salary' && creditEntitiesOf(lookups, realId(v.target)).length > 0;
  return {
    type: 'rate',
    target: rateTarget(v),
    price: v.tkind === 'assign' ? assignPrice(v, format, currency) : price(v.rateV, format, currency),
    from: v.from ?? '',
    creditEntities: offersCredit ? listOf(v.credit) : null,
  };
}

/** The event the form describes; null while a date is missing (nothing to preview). */
export function buildEvent(state: FormState, lookups: CaptureLookups): CaptureEventInput | null {
  const v = state.values;
  const cur = (key: string) => currencyOf(key, state, lookups) as CapturePrice['currency'];
  switch (state.type) {
    case 'join':
      if (!v.from) return null;
      return {
        type: 'join',
        person: orNull(v.person),
        project: orNull(v.project),
        from: v.from,
        pay: assignPrice(v, (v.rateFmt || 'hourly') as PriceFormat, cur('rateV')),
        planMinutes: parseMinutes(v.plan),
      };
    case 'leave':
      return v.to ? { type: 'leave', assignment: orNull(v.assignment), to: v.to } : null;
    case 'project':
      if (!v.from) return null;
      return {
        type: 'project',
        name: v.name ?? '',
        client: orNull(v.client),
        from: v.from,
        billing: v.billing === 'fixed' ? 'fixed' : 'hourly',
        price: price(v.rateV, v.billing === 'fixed' ? 'monthly' : 'hourly', cur('rateV')),
        contract: orNull(v.contract),
        person: orNull(v.person),
      };
    case 'rate':
      return v.from ? rateEvent(state, lookups) : null;
    case 'employ':
      return v.from
        ? {
            type: 'employ',
            person: orNull(v.person),
            // An entity the person is already employed in is not offered (V237), e.g. kept from «Увольнение».
            entity: lookups.people.find((p) => p.id === v.person)?.entityIds.includes(v.entity ?? '') ? null : orNull(v.entity),
            netMinor: parseAmount(v.netV, 'monthly'),
            advanceMinor: parseAmount(v.advanceV, 'monthly') ?? 0,
            currency: cur('netV'),
            from: v.from,
          }
        : null;
    case 'person':
      return {
        type: 'person',
        person: orNull(v.person),
        kind: v.kind === 'employee' ? 'employee' : 'contractor',
        entity: orNull(v.entity),
        from: orNull(v.from),
        project: orNull(v.project),
        pay: realId(v.project) ? assignPrice(v, (v.rateFmt || 'hourly') as PriceFormat, cur('rateV')) : null,
      };
    case 'status':
      return v.date
        ? {
            type: 'status',
            project: orNull(v.project),
            status: (v.status || 'paused') as 'paused' | 'ended' | 'active',
            date: v.date,
          }
        : null;
    case 'payment':
      return v.date
        ? {
            type: 'payment',
            client: orNull(v.client),
            invoice: orNull(v.invoice),
            amountMinor: parseAmount(v.amount, 'monthly'),
            currency: cur('amount'),
            feeMinor: parseAmount(v.fee, 'monthly') ?? 0,
            date: v.date,
          }
        : null;
    case 'note':
      return { type: 'note', text: v.text ?? '', ref: orNull(v.ref) };
    case 'dismiss':
      return v.lastDay ? { type: 'dismiss', person: orNull(v.person), entity: orNull(v.entity), lastDay: v.lastDay, severance: severanceOf(v.severance) } : null;
    case 'absence':
      // The last day before the first is not an event yet (the server refuses it, V233).
      return v.from && v.to && v.from <= v.to
        ? {
            type: 'absence',
            person: orNull(v.person),
            kind: v.kind === 'unpaid' ? 'unpaid' : 'vacation',
            from: v.from,
            to: v.to,
            assignments: listOf(v.assignments),
            // An entity of a project ticked off is hidden and not sent (V235).
            entities: listOf(v.entities).filter((id) => leaveEntityIds(lookups, listOf(v.assignments)).includes(id)),
          }
        : null;
    case 'edit':
      return { type: 'edit', person: orNull(v.person), name: v.name ?? '', location: orNull(v.location) };
    case 'extra':
      // The amount is on hand in the salary's currency (V287); the server takes the currency from the salary.
      return v.date
        ? { type: 'extra', person: orNull(v.person), entity: orNull(v.entity), kind: v.kind === 'aid' ? 'aid' : 'bonus', amountMinor: parseAmount(v.amount, 'monthly'), date: v.date }
        : null;
    case 'client':
      return {
        type: 'client',
        name: v.name ?? '',
        codeName: orNull(v.codeName ?? ''),
        country: orNull(v.country ?? ''),
        currency: (v.currency || DEFAULT_CURRENCY) as CapturePrice['currency'],
      };
    default:
      return null;
  }
}
